"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconPhoto,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function DeepAutumnVsWarmAutumnContent() {
  const t = useTranslations("deepAutumnVsWarmAutumn");

  // Get the subseason data
  const deepAutumnData = colorSeasons.AUTUMN.subseasons.DEEP;
  const warmAutumnData = colorSeasons.AUTUMN.subseasons.TRUE; // TRUE is Warm Autumn in the constants

  // State for outfit data
  const [deepAutumnOutfits, setDeepAutumnOutfits] = useState<OutfitIdea[]>([]);
  const [warmAutumnOutfits, setWarmAutumnOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [deepAutumnDoc, warmAutumnDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "AUTUMN_DEEP")),
          getDoc(doc(db, "outfitIdeas", "AUTUMN_TRUE")),
        ]);

        setDeepAutumnOutfits(deepAutumnDoc.data()?.ideas || []);
        setWarmAutumnOutfits(warmAutumnDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const comparisonPoints = [
    {
      aspect: "Color Depth",
      deepAutumn: "Rich, deep colors with strong saturation and intensity",
      warmAutumn: "Warm, golden colors with medium depth and warmth",
      icon: IconPalette,
    },
    {
      aspect: "Undertone",
      deepAutumn: "Warm undertones with deep, rich characteristics",
      warmAutumn: "Warm undertones with golden, earthy characteristics",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      deepAutumn: "Deep, rich coloring with high contrast",
      warmAutumn: "Warm, golden coloring with natural warmth",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      deepAutumn: "Light, pastel colors",
      warmAutumn: "Cool, icy colors",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Deep Autumn vs Warm Autumn
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Discover the key differences between these two warm autumn subseasons and find which one perfectly
            complements your natural coloring.
          </Text>

          {/* Season Moodboard */}
          <Box mb="xl">
            <Box
              mih={{ base: "300px", md: "400px" }}
              style={{
                position: "relative",
                borderRadius: "1rem",
                overflow: "hidden",
              }}
            >
              <Image
                src="/AUTUMN.webp"
                alt="Autumn season moodboard"
                width={800}
                height={400}
                style={{
                  borderRadius: "1rem",
                  objectFit: "cover",
                  objectPosition: "center",
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison Overview
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{deepAutumnData.emoji}</Text>
                <Title order={3}>Deep Autumn</Title>
                <Text size="sm" c="dimmed">
                  {deepAutumnData.description.substring(0, 150)}...
                </Text>
                <Badge color="orange" variant="light">
                  Rich & Deep
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{warmAutumnData.emoji}</Text>
                <Title order={3}>Warm Autumn</Title>
                <Text size="sm" c="dimmed">
                  {warmAutumnData.description.substring(0, 150)}...
                </Text>
                <Badge color="orange" variant="light">
                  Warm & Golden
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="orange" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="orange" size="sm" mb="xs">
                      Deep Autumn:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.deepAutumn}
                    </Text>
                    <Text fw={500} c="orange" size="sm" mb="xs">
                      Warm Autumn:
                    </Text>
                    <Text size="sm">{point.warmAutumn}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Detailed Color Palette Comparison */}
        <Tabs defaultValue="deep-autumn" variant="outline">
          <Tabs.List justify="center" mb="xl">
            <Tabs.Tab value="deep-autumn" leftSection={<IconDroplet size={16} />}>
              Deep Autumn Palette
            </Tabs.Tab>
            <Tabs.Tab value="warm-autumn" leftSection={<IconDroplet size={16} />}>
              Warm Autumn Palette
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="deep-autumn">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Deep Autumn Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Rich, deep colors with strong saturation and intensity for warm undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {deepAutumnData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {deepAutumnData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {deepAutumnData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="warm-autumn">
            <Card withBorder p="xl" radius="md">
              <Title order={2} mb="lg" ta="center">
                Warm Autumn Color Palette
              </Title>
              <Text size="sm" c="dimmed" ta="center" mb="xl">
                Warm, earthy colors with medium saturation that enhance warm undertones
              </Text>

              <Stack gap="xl">
                {/* Primary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Primary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {warmAutumnData.primaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Neutral Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Neutral Colors
                  </Title>
                  <SimpleGrid cols={{ base: 4, md: 6 }} spacing="sm">
                    {warmAutumnData.neutrals.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Complementary Colors */}
                <Box>
                  <Title order={3} mb="md">
                    Complementary Colors
                  </Title>
                  <SimpleGrid cols={{ base: 3, md: 4 }} spacing="sm">
                    {warmAutumnData.complementaryColors.map((color, index) => (
                      <Paper key={index} p="sm" withBorder radius="md">
                        <Box
                          style={{
                            backgroundColor: color.hex,
                            height: 60,
                            borderRadius: 8,
                            border: "1px solid #e0e0e0",
                            marginBottom: 8,
                          }}
                        />
                        <Text size="xs" fw={500} ta="center">
                          {color.name}
                        </Text>
                        <Text size="xs" c="dimmed" ta="center">
                          {color.hex}
                        </Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Characteristics Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Characteristics & Styling
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} mb="md" ta="center" c="orange">
                Deep Autumn
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {deepAutumnData.characteristics.map((char, index) => (
                      <Badge key={index} color="orange" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {deepAutumnData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center" c="orange">
                Warm Autumn
              </Title>
              <Stack gap="md">
                <Box>
                  <Text fw={500} mb="sm">
                    Characteristics:
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    {warmAutumnData.characteristics.map((char, index) => (
                      <Badge key={index} color="orange" variant="light" size="sm">
                        {char}
                      </Badge>
                    ))}
                  </SimpleGrid>
                </Box>
                <Box>
                  <Text fw={500} mb="sm">
                    Sample Clothing:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {warmAutumnData.sampleClothing}
                  </Text>
                </Box>
              </Stack>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Outfit Samples */}
        <Divider my="xl" />
        <Box>
          <Title order={2} mb="lg" ta="center">
            Outfit Inspiration
          </Title>
          <Text size="sm" c="dimmed" ta="center" mb="xl">
            See how these color palettes translate into real outfit combinations
          </Text>

          <Tabs defaultValue="deep-autumn-outfits" variant="outline">
            <Tabs.List justify="center" mb="xl">
              <Tabs.Tab value="deep-autumn-outfits" leftSection={<IconPhoto size={16} />}>
                Deep Autumn Outfits
              </Tabs.Tab>
              <Tabs.Tab value="warm-autumn-outfits" leftSection={<IconPhoto size={16} />}>
                Warm Autumn Outfits
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="deep-autumn-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : deepAutumnOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={deepAutumnOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Deep Autumn</Text>
                </Box>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="warm-autumn-outfits">
              {loading ? (
                <Box ta="center" py="xl">
                  <Text>Loading outfit samples...</Text>
                </Box>
              ) : warmAutumnOutfits.length > 0 ? (
                <OutfitGrid outfitIdeas={warmAutumnOutfits} />
              ) : (
                <Box ta="center" py="xl">
                  <Text c="dimmed">No outfit samples available for Warm Autumn</Text>
                </Box>
              )}
            </Tabs.Panel>
          </Tabs>
        </Box>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Depth Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a rich chocolate brown vs a warm caramel brown near your face:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Deep Autumn:</strong> The chocolate brown enhances your richness</List.Item>
                <List.Item><strong>Warm Autumn:</strong> The caramel brings out your warmth better</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Contrast Check</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Look at the contrast between your hair and skin:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Deep Autumn:</strong> High contrast, often dark hair with lighter skin</List.Item>
                <List.Item><strong>Warm Autumn:</strong> Medium contrast, features blend warmly</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="orange.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between Deep Autumn and Warm Autumn.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/autumn/deep"
                size="sm"
                color="gray"
              >
                View Deep Autumn Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/autumn/true"
                size="sm"
                color="gray"
              >
                View Warm Autumn Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
